import NProgress from "nprogress"; // progress bar
import { cacheWrapper } from "@i.un/libs";

import { getLinkedinCookieParams, useTranslate } from "@/libs/utils";
import { sendSentryError } from "@wltech/hooks/src/useSentryConfig";
import { allowList } from "@/config";
import { ChromeCache, type TChromeCache } from "@/libs/ChromeCache";
import { getAppBridgeStore } from "@/libs/LocalStore";
import { getExtensionInfo } from "@/libs/chrome";
import { getFingerprintHeaders } from "@/libs/fingerprint";

// HTTP 状态码常量
export const HTTP_STATUS = {
  NETWORK_ERROR: 0, // 请求未到达服务端（断网 / 超时 / 解析失败等），与 XHR 的 status 0 语义一致
  BAD_REQUEST: 400,
  LOGIN_INVALID: 401,
  INTERNAL_SERVER_ERROR: 500,
};

// fetch 链路里抛出的异常分类，用于给用户可操作的提示
export type RequestErrorKind =
  | "offline" // navigator.onLine === false
  | "network" // fetch 本身 reject：DNS / 连接被拒 / CORS 等
  | "timeout" // AbortController 超时或主动取消
  | "parse" // 响应体不是合法 JSON
  | "unknown";

function classifyRequestError(err: unknown): RequestErrorKind {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "offline";
  }
  if (err instanceof DOMException && err.name === "AbortError") {
    return "timeout";
  }
  // Chrome: "Failed to fetch" / Firefox: "NetworkError when attempting to fetch resource." / Safari: "Load failed"
  if (
    err instanceof TypeError &&
    /fetch|network|load failed/i.test(err.message)
  ) {
    return "network";
  }
  if (err instanceof SyntaxError) {
    return "parse";
  }
  return "unknown";
}

const REQUEST_ERROR_I18N_KEY: Record<RequestErrorKind, string> = {
  offline: "network_offline",
  network: "network_unreachable",
  timeout: "request_timeout",
  parse: "something_wrong",
  unknown: "something_wrong",
};

// 语言包尚未加载 / key 缺失时的英文兜底
const REQUEST_ERROR_FALLBACK: Record<RequestErrorKind, string> = {
  offline: "You're offline. Check your internet connection and try again.",
  network:
    "Unable to connect to the server. Check your internet connection and try again.",
  timeout: "The request timed out. Please try again.",
  parse: "Something went wrong, please try again later.",
  unknown: "Something went wrong, please try again later.",
};

function getRequestErrorMessage(kind: RequestErrorKind): string {
  const key = REQUEST_ERROR_I18N_KEY[kind];
  try {
    const translated = useTranslate()?.(key);
    // chrome.i18n 缺 key 返回 ""；vue-i18n 缺 key 返回 key 本身
    if (translated && translated !== key) {
      return translated;
    }
  } catch {
    // 忽略，走兜底
  }
  return REQUEST_ERROR_FALLBACK[kind];
}

let commonParams:
  | { locale: string; extId: string; version: string }
  | undefined;

let cookieParams: { cookie: string; csrfToken: string } | undefined;

function getCommonParams() {
  if (__PLATFORM__ === "extension") {
    commonParams = undefined;
  } else {
    const appBridgeStore = getAppBridgeStore();
    commonParams = appBridgeStore.get("ExtensionInfo");
  }
}

async function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function getCookieParams() {
  if (__PLATFORM__ === "extension") {
    cookieParams = await getLinkedinCookieParams();
    return;
  }
  if (!cookieParams?.cookie) {
    const timeout = 10;
    await delay(timeout);

    cookieParams = getAppBridgeStore().get("CookieParams");
    if (!cookieParams) {
      const oldCookieParams = localStorage.getItem("commonParams");
      if (oldCookieParams) {
        cookieParams = JSON.parse(oldCookieParams);
      }
    }
  }
}

export const baseURL = import.meta.env.VITE_APP_API; // "https://demo.linkedcrm.ai";
export const VITE_MODE = import.meta.env.VITE_MODE;

const headers = new Headers();
headers.append("Content-Type", "application/json");
export { headers };
const fetchOption: RequestInit = {
  credentials: "include",
  method: "post",
  headers: headers,
  redirect: "follow",
};

interface Setting {
  loading?: boolean;
  loadingDelay?: number; // 至少加载多少毫秒
  needCache?: boolean;
  chromeCacheKey?: keyof TChromeCache;
  chromeCacheTtl?: number; // 缓存时间 单位毫秒 默认1天
}
const defalutSetting: Setting = {
  loading: false,
  loadingDelay: 0,
  needCache: false,
};

export type ResponseType<T = any> = {
  code: number;
  msg: string;
  data: T;
};

// 所有接口请求的统一出口，附加指纹请求头
export async function baseFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  const fpHeaders = await getFingerprintHeaders();
  Object.entries(fpHeaders).forEach(([key, value]) => headers.set(key, value));
  return fetch(input, { ...init, headers });
}

export function addParams(url: string, params: any = {}) {
  if (Object.keys(params).length > 0) {
    if (url.includes("?")) {
      url += "&" + new URLSearchParams(params).toString();
    } else {
      url += "?" + new URLSearchParams(params).toString();
    }
  }
  return url;
}

export function addDevTimezone(url: string) {
  return addParams(url, VITE_MODE === "dev" ? { time: Date.now() } : {});
}

export async function fetch2<T = any>(
  url: string,
  params: any = {},
  setting = defalutSetting,
  needCookie = false,
): Promise<ResponseType<T>> {
  const _setting = Object.assign({}, defalutSetting, setting);
  if (_setting.loading) {
    NProgress.configure({
      showSpinner: true,
      trickle: false,
      template: `<div role="bar"></div>
          <div class="spinner" role="spinner">
            <div class="spinner-wrap">
              <div class="spinner-icon"></div>
            </div>
          </div>`,
    });
    NProgress.start();
  }

  if (
    __PLATFORM__ === "extension" &&
    _setting.chromeCacheKey &&
    chrome.storage
  ) {
    const cache = await ChromeCache.get(_setting.chromeCacheKey);
    if (cache) {
      return cache;
    }
  }

  if (!cookieParams) {
    await getCookieParams();
  }
  if (!commonParams) {
    getCommonParams();
  }
  let data: any = Object.assign(
    { userAgent: navigator.userAgent },
    commonParams,
    params,
  );
  if (needCookie) {
    data = Object.assign({}, data, cookieParams);
  }

  const startTime = performance.now();
  const fun = _setting.needCache ? cacheWrapper(baseFetch) : baseFetch;
  const method = ((_setting as any).method || "post").toUpperCase();
  if (method === "GET") {
    url = addParams(url, data);
  }
  const body = method === "POST" ? JSON.stringify(data) : undefined;
  return fun(addDevTimezone(url), {
    ...fetchOption,
    method: method,
    body: body,
  })
    .then((res) => {
      const loadTime = performance.now() - startTime;
      if (!res.ok) {
        // throw new Error('Network response was not ok');
        const errorType =
          res.status === HTTP_STATUS.INTERNAL_SERVER_ERROR
            ? "ERR_RESPONSE_500"
            : "ERR_RESPONSE";
        sendSentryError(
          {
            err: {
              status: res.status,
              statusText: res.statusText,
            },
            url,
            data,
            message: "fetch !res.ok",
          },
          errorType,
          res.status ?? "NO_CODE",
        );
        return Promise.resolve({ code: res.status, msg: res.statusText });
      }

      const contentType = res.headers.get("Content-Type") ?? "";
      let results: Promise<any>;
      if (contentType.includes("application/json")) {
        results = res.json();
      } else if (
        contentType.includes("blob") ||
        contentType.includes("octet-stream") ||
        contentType.includes("application/vnd.ms-excel")
      ) {
        results = res.blob();
      } else {
        results = res.text();
      }

      if (_setting.loadingDelay && _setting.loadingDelay > loadTime) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(results);
          }, _setting.loadingDelay! - loadTime);
        });
      }
      return results;
    })
    .then((result) => {
      if (
        __PLATFORM__ === "extension" &&
        _setting.chromeCacheKey &&
        chrome.storage &&
        result.code === 200
      ) {
        ChromeCache.set(_setting.chromeCacheKey, result, {
          ttl: _setting.chromeCacheTtl,
        });
      }
      // 登录失效
      if (result && result.code === HTTP_STATUS.LOGIN_INVALID) {
        const currentPath = window.location.href;
        const isInAllowList = allowList.some((item) =>
          currentPath.toLowerCase().includes(item.toLowerCase()),
        );
        if (!isInAllowList) {
          const encodedCurrentPath = encodeURIComponent(currentPath);
          window.location.href = `${
            import.meta.env.VITE_APP_DOMAIN
          }/login?logout&redirect=${encodedCurrentPath}`;
          return {
            code: result.code,
            msg: result.msg || "Not logged in",
          };
        }
      }
      return result;
    })
    .catch((err) => {
      const errorKind = classifyRequestError(err);
      sendSentryError(
        { err, url, data, errorKind, message: `fetch .catch [${errorKind}]` },
        "ERR_REQUEST",
        errorKind, // Sentry tag errorType，便于按 offline / network / timeout 筛选
      );
      return Promise.resolve({
        code: HTTP_STATUS.NETWORK_ERROR,
        msg: getRequestErrorMessage(errorKind),
        errorKind,
        error: err,
        requestUrl: url,
      });
    })
    .finally(() => {
      if (_setting.loading) {
        NProgress.done();
      }
    });
}

export const post = fetch2;
export const get = <T = any>(
  url: string,
  params: any = {},
  setting = defalutSetting,
  needCookie = false,
) => {
  return fetch2<T>(
    url,
    params,
    { ...setting, method: "get" } as any,
    needCookie,
  );
};
